/**
 * Raw Material Deliveries Hook
 * React Query hooks for raw material delivery operations
 *
 * @author SAUBHAGYA Development Team
 * @since Raw Material Deliveries Module
 *
 * Includes hooks for:
 * - Scheduled deliveries (legacy - from routes endpoint)
 * - Incoming deliveries (new - from mfg_incoming_delivery table)
 */

import { useQuery } from '@tanstack/react-query';
import {
  rawMaterialDeliveryApi,
  RawMaterialDeliveryDTO,
  IncomingDeliveryDTO,
  IncomingDeliveryStats,
} from '@/services/manufacturing/rawMaterialDeliveryApi';

/**
 * Hook to fetch scheduled/active deliveries for the current manufacturing unit
 * Uses the existing working API endpoint: /api/v1/manufacturing/{id}/routes/scheduled
 */
export function useScheduledDeliveries(enabled: boolean = true) {
  return useQuery<RawMaterialDeliveryDTO[], Error>({
    queryKey: ['scheduled-deliveries'],
    queryFn: () => rawMaterialDeliveryApi.getScheduledDeliveries(),
    enabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch active incoming deliveries (UPCOMING or IN_TRANSIT)
 * Uses the new mfg_incoming_delivery table
 */
export function useActiveIncomingDeliveries(enabled: boolean = true) {
  return useQuery<IncomingDeliveryDTO[], Error>({
    queryKey: ['active-incoming-deliveries'],
    queryFn: () => rawMaterialDeliveryApi.getActiveIncomingDeliveries(),
    enabled,
    staleTime: 15 * 1000, // 15 seconds - more frequent updates for real-time tracking
    refetchInterval: 30 * 1000, // Auto-refresh every 30 seconds
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch today's incoming deliveries
 */
export function useTodayIncomingDeliveries(enabled: boolean = true) {
  return useQuery<IncomingDeliveryDTO[], Error>({
    queryKey: ['today-incoming-deliveries'],
    queryFn: () => rawMaterialDeliveryApi.getTodayIncomingDeliveries(),
    enabled,
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch incoming delivery statistics
 */
export function useIncomingDeliveryStats(enabled: boolean = true) {
  return useQuery<IncomingDeliveryStats, Error>({
    queryKey: ['incoming-delivery-stats'],
    queryFn: () => rawMaterialDeliveryApi.getIncomingDeliveryStats(),
    enabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
}
